/**
 * The certificate and the domain, side by side: when each runs out and when we
 * last looked.
 *
 * Both are looked at once a day, so each card says when that was. "Not known"
 * is its own state and never reads as fine: a failed registry lookup keeps the
 * last date it had (the engine does not overwrite a known expiry with nothing),
 * and a site that has never been looked at has none yet.
 *
 * A site with no certificate row is either on plain http or on https and not
 * looked at yet (a site added a minute ago). The row cannot tell the two apart,
 * so the address does: only an http address is called "not on https".
 *
 * A web maintenance package can leave a look out (docs/saidiseire-paketid.md).
 * Such a card says so first; a date an earlier look found stays below it as
 * the last one known, since nothing will refresh it.
 */
import { Globe, Lock } from "lucide-react";
import {
  certVerdictKey,
  expiryLevel,
  expiryTone,
  type ExpiryKind,
  type MonitorExpiryView,
} from "../../../lib/monitoring";
import { CardHead, StatusPill, fmtDate, fmtDateTime } from "../../ui";
import { expiryText, type T } from "./words";

function ExpiryCard({
  kind,
  view,
  https,
  on,
  t,
}: {
  kind: ExpiryKind;
  view: MonitorExpiryView | null;
  https: boolean;
  /** The site's package includes this look. */
  on: boolean;
  t: T;
}) {
  const Icon = kind === "cert" ? Lock : Globe;
  const level = expiryLevel(view, kind);
  // The verdict says more than the date when the certificate is wrong in a way
  // no date explains - issued for another name, or not trusted at all.
  const verdict = kind === "cert" && view?.verdict !== "valid" ? certVerdictKey(view?.verdict ?? null) : null;

  return (
    <section className="crm-card">
      <CardHead
        title={
          <span className="inline-flex items-center gap-2">
            <Icon className="size-4 text-muted-foreground" strokeWidth={1.8} />
            {t(`monitoring.${kind}.title`)}
          </span>
        }
        actions={
          !on ? (
            <StatusPill tone="mut">{t("monitoring.expiry.notInPlanPill")}</StatusPill>
          ) : view ? (
            <StatusPill tone={expiryTone(level)}>{expiryText(t, view)}</StatusPill>
          ) : null
        }
      />
      <div className="space-y-1 px-[26px] pb-5 text-[13px]">
        {!on ? <p className="crm-mut">{t("monitoring.expiry.notInPlan")}</p> : null}
        {!view ? (
          on ? (
            <p className="crm-mut">
              {kind === "cert" && https ? t("monitoring.expiry.neverChecked") : t(`monitoring.${kind}.none`)}
            </p>
          ) : null
        ) : (
          <>
            <p>
              {view.at
                ? t(`monitoring.${kind}.until`, { date: fmtDate(view.at) })
                : t("monitoring.expiry.unknown")}
            </p>
            {verdict && view.verdict !== "expired" ? <p className="crm-mut">{t(verdict)}</p> : null}
            <p className="crm-mut text-[12px]">
              {view.checkedAt
                ? t("monitoring.expiry.checkedAt", { when: fmtDateTime(view.checkedAt) })
                : t("monitoring.expiry.neverChecked")}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

export function ExpiryCards({
  url,
  cert,
  domain,
  certOn = true,
  domainOn = true,
  t,
}: {
  /** The watched address: whether a missing certificate means "http" or "not yet". */
  url: string;
  cert: MonitorExpiryView | null;
  domain: MonitorExpiryView | null;
  /** The site's package includes the certificate look (`MonitorPolicyView.certExpiry`). */
  certOn?: boolean;
  domainOn?: boolean;
  t: T;
}) {
  const https = url.startsWith("https:");
  // mb-4 like every card on the page, so the card after these (the monthly report) needs no margin of its own.
  return (
    <div className="mb-4 grid gap-4 md:grid-cols-2">
      <ExpiryCard kind="cert" view={cert} https={https} on={certOn} t={t} />
      <ExpiryCard kind="domain" view={domain} https={https} on={domainOn} t={t} />
    </div>
  );
}
